export class ApiError extends Error {
  constructor(
    message: string,
    public status: number,
    public body?: unknown,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

export class ApiClient {
  private baseUrl: string;

  constructor(baseUrl: string) {
    this.baseUrl = baseUrl.replace(/\/$/, "");
  }

  async get<T>(
    path: string,
    params?: Record<string, string | number | boolean | undefined | null>,
    signal?: AbortSignal,
  ): Promise<T> {
    const url = this.buildUrl(path, params);
    return this.request<T>(url, { method: "GET", signal });
  }

  async post<T>(path: string, body?: unknown, signal?: AbortSignal): Promise<T> {
    const url = this.buildUrl(path);
    return this.request<T>(url, {
      method: "POST",
      body: body !== undefined ? JSON.stringify(body) : undefined,
      signal,
    });
  }

  async patch<T>(path: string, body?: unknown, signal?: AbortSignal): Promise<T> {
    const url = this.buildUrl(path);
    return this.request<T>(url, {
      method: "PATCH",
      body: body !== undefined ? JSON.stringify(body) : undefined,
      signal,
    });
  }

  async put<T>(path: string, body?: unknown, signal?: AbortSignal): Promise<T> {
    const url = this.buildUrl(path);
    return this.request<T>(url, {
      method: "PUT",
      body: body !== undefined ? JSON.stringify(body) : undefined,
      signal,
    });
  }

  async delete<T>(
    path: string,
    body?: unknown,
    signal?: AbortSignal,
  ): Promise<T> {
    const url = this.buildUrl(path);
    return this.request<T>(url, {
      method: "DELETE",
      body: body !== undefined ? JSON.stringify(body) : undefined,
      signal,
    });
  }

  private buildUrl(
    path: string,
    params?: Record<string, string | number | boolean | undefined | null>,
  ): string {
    const fullPath = `${this.baseUrl}${path}`;
    let url: URL;
    try {
      url = new URL(fullPath);
    } catch {
      url = new URL(fullPath, window.location.origin);
    }
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          url.searchParams.set(key, String(value));
        }
      });
    }
    return url.toString();
  }

  private async request<T>(url: string, options: RequestInit): Promise<T> {
    try {
      const response = await fetch(url, {
        ...options,
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          ...options.headers,
        },
      });

      if (response.status === 401) {
        const currentPath = window.location.pathname;
        const isAuthEndpoint = url.includes("/api/sso/");
        const alreadyOnLogin = currentPath === "/login";
        // Only hard-redirect to /login if we're NOT already there and the 401
        // didn't come from an auth endpoint (which expects 401s during login).
        if (!alreadyOnLogin && !isAuthEndpoint) {
          window.location.href = "/login";
        }
        throw new ApiError("Authentication required", 401);
      }

      if (response.status === 204) {
        return undefined as T;
      }

      const contentType = response.headers.get("Content-Type") ?? "";
      const isJson = contentType.includes("application/json");
      const rawText = await response.text();
      let body: unknown = rawText;
      if (isJson && rawText) {
        try {
          body = JSON.parse(rawText);
        } catch {
          body = rawText;
        }
      }

      if (!response.ok) {
        if (response.status >= 500) {
          throw new ApiError(
            typeof body === "object" && body !== null && "message" in body
              ? (body as { message: string }).message
              : "Server error",
            response.status,
            body,
          );
        }
        throw new ApiError(
          typeof body === "object" && body !== null && "message" in body
            ? (body as { message: string }).message
            : `Request failed with status ${response.status}`,
          response.status,
          body,
        );
      }

      return body as T;
    } catch (error) {
      if (error instanceof ApiError) {
        throw error;
      }
      if (
        typeof error === "object" &&
        error !== null &&
        "name" in error &&
        (error as { name: string }).name === "AbortError"
      ) {
        throw error;
      }
      throw new ApiError("Network request failed", 0);
    }
  }
}

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL ?? "";

export const apiClient = new ApiClient(API_BASE_URL);
